import { useEffect } from 'react';
import { map, omit, forEach } from 'lodash';

import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Container, Form, Button, Spinner } from 'react-bootstrap';
import { ApplicantInfo } from '../../components/ApplicantInfo';
import { ClientError } from '../../components/Errors/ClientError';
import { SomethingBroke } from '../../components/Errors/SomethingBroke';
import { PageHeader } from '../../components/Layout/PageHeader';
import PageLoading from '../../components/PageLoading';

import useQuery from '../../services/useQuery';
import { applicantSchema } from '../../schemas/applicantSchema';
import { siteInfo, siteAddress } from '../../schemas/burnSiteSchema';
import { BurnType } from '../../components/BurnPermit/BurnType';
import { BurnSite } from '../../components/BurnPermit/BurnSite';
import useAPI from '../../services/useAPI';

const pageSchema = yup
	.object()
	.concat(applicantSchema)
	.shape({ burnSite: siteAddress })
	.concat(siteInfo);

const prepareFormData = data => {
	data.address = data.mailingAddress;
	delete data.mailingAddress;

	data.burnSite = {};
	data.burnSite.address = data.burnSiteAddress;
	data.burnSite.city = data.burnSiteCity;
	data.burnSite.state = data.burnSiteState;
	data.burnSite.zip = data.burnSiteZip;
	data.parcelNumbers = map(data.parcelNumbers, num => ({
		parcelNumber: num,
	}));
	delete data.burnSiteAddress;
	delete data.burnSiteCity;
	delete data.burnSiteState;
	delete data.bunSiteZip;

	return data;
};

export default function EditBurnPermit() {
	const query = useQuery();
	const { register, handleSubmit, reset, formState, control } = useForm({
		resolver: yupResolver(pageSchema),
	});

	const id = query.get('id');
	const [{ isLoading, data, error }, refetchDataFrom] = useAPI(
		`/burn-permit/${id}`
	);
	const [
		{ isLoading: isSubmitting, reqComplete: submitted, error: submitError },
		submit,
	] = useAPI();

	useEffect(() => {
		if (data) reset(prepareFormData(data));
	}, [data, reset]);

	const onSubmit = formData => {
		forEach(formData.burnSite, (value, key) => {
			formData[
				'burnSite' + key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()
			] = value;
		});
		delete formData.burnSite;
		formData.mailingAddress = formData.address;
		delete formData.address;

		formData.parcelNumbers = formData.parcelNumbers.map(
			({ parcelNumber }) => parcelNumber
		);

		submit({
			url: `/burn-permit/${id}`,
			method: 'PUT',
			body: formData,
			onSuccess: () => {
				refetchDataFrom(`/burn-permit/${id}`);
			},
		});
	};

	if (!query.has('id')) {
		return (
			<ClientError>
				Unable to edit application. Application ID not specified in URL query.
			</ClientError>
		);
	}

	if (error) {
		return <SomethingBroke />;
	}

	return (
		<Container fluid className="p-0 mb-5 permit">
			<PageHeader title="Burn Permit" />
			<div className="app-container">
				{isLoading && !submitted ? (
					<PageLoading />
				) : (
					<Form onSubmit={handleSubmit(onSubmit)}>
						<ApplicantInfo
							formComponent
							register={register}
							formState={formState}
						/>
						<Controller
							name="burnType"
							control={control}
							render={({ field }) =>
								field.value ? (
									<BurnType
										compact
										formComponent
										currBurntype={field.value}
										{...omit(field, 'ref', 'value')}
									/>
								) : null
							}
						/>
						<BurnSite
							formComponent
							prefix="burnSite"
							control={control}
							register={register}
							formState={formState}
						/>
						<Container>
							<Form.Group>
								<Button
									type="submit"
									size="lg"
									variant="primary"
									disabled={isSubmitting}>
									{isSubmitting && <Spinner animation="border" size="sm" />}{' '}
									Submit
								</Button>
								{submitted && submitError && (
									<Form.Text
										className="text-danger"
										style={{ 'font-size': '1.25rem' }}>
										Submit Error:{' '}
										{submitError.responseData ?? submitError.message}
									</Form.Text>
								)}
								{submitted && !submitError && (
									<Form.Text
										className="text-success"
										style={{ 'font-size': '1.25rem' }}>
										Save success!
									</Form.Text>
								)}
							</Form.Group>
						</Container>
					</Form>
				)}
			</div>
		</Container>
	);
}
